import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { LeaderboardItemBackend } from '@api'
import { Loader } from '@ds'
import { DEFAULT_AVATAR_URL } from '@shared'

import { generateRandomString } from '../../home/helpers'
import styles from './Leaderboard.module.scss'

import { SquadListItemDefault } from './SquadListItemDefault'
import { SquadListItemRanking } from './SquadListItemRanking'
import classNames from 'classnames'
import { useLeaderboardState, useNavbarVisible } from '@store'

interface RatingGroup {
  rating: number
  indices: number[]
  colorIndex: number | null
}

const groupLeaderboardByRating = (
  leaderboardData: LeaderboardItemBackend['leaderboard'] | []
): Map<number, RatingGroup> => {
  const ratingMap = new Map<number, RatingGroup>()

  leaderboardData.forEach((item, index) => {
    const rating = item.rating

    if (!ratingMap.has(rating)) {
      ratingMap.set(rating, {
        rating,
        indices: [],
        colorIndex: null,
      })
    }

    ratingMap.get(rating)!.indices.push(index)
  })

  let colorIndexCounter = 0

  ratingMap.forEach((group) => {
    if (group.indices.length > 1) {
      group.colorIndex = colorIndexCounter % 3
      colorIndexCounter++
    } else {
      group.colorIndex = null
    }
  })

  return ratingMap
}

const getGroupColorIndex = (
  rating: number,
  ratingGroups: Map<number, RatingGroup>
): number | null => {
  const group = ratingGroups.get(rating)
  return group ? group.colorIndex : null
}

const isFirstInGroup = (
  index: number,
  rating: number,
  ratingGroups: Map<number, RatingGroup>
): boolean => {
  const group = ratingGroups.get(rating)
  if (!group || group.indices.length <= 1) {
    return false
  }
  return group.indices[0] === index
}

const isLastInGroup = (
  index: number,
  rating: number,
  ratingGroups: Map<number, RatingGroup>
): boolean => {
  const group = ratingGroups.get(rating)
  if (!group || group.indices.length <= 1) {
    return false
  }
  const lastIndex = group.indices[group.indices.length - 1]
  return lastIndex === index
}

export const Leaderboard = () => {
  const { t } = useTranslation()
  const { leaderboard, me, isLoading } = useLeaderboardState()
  const [showPinnedBottom, setShowPinnedBottom] = useState(false)
  const shouldShowNavbar = useNavbarVisible()

  const { ref: currentUserRef } = useInView({
    threshold: 0.2,
    rootMargin: '0px',
    initialInView: true,
    onChange: (inView) => {
      setShowPinnedBottom(!inView)
    },
  })

  const ratingGroups = useMemo(() => groupLeaderboardByRating(
    leaderboard || []
  ), [leaderboard])

  const currentUser = useMemo(() => {
    const username = me.username ?? 'You';

    const meFromListIndex = leaderboard.findIndex((item) => item.username === username);
    const rank = meFromListIndex > -1 ? meFromListIndex + 1 : me.position;
    return {
      username: me.username,
      rating: me.rating,
      avatar_icon: me.avatar_asset.avatar_icon,
      avatar_effect: me.avatar_asset.avatar_effect,
      rank,
      groupColorIndex: getGroupColorIndex(me.rating, ratingGroups),
      isFirst: isFirstInGroup(me.position, me.rating, ratingGroups),
      isLast: isLastInGroup(me.position, me.rating, ratingGroups),
      isCurrentUser: true,
    }
  }, [me, leaderboard, ratingGroups])

  const leaderboardDataList = useMemo(
    () => {
      if (!currentUser) return [];
      const list = leaderboard.map((item, index) => {
        const isCurrentUser = item.username === currentUser.username || 'You' === currentUser.username;
        const username = item.username ?? generateRandomString(6);
        const rank = index + 1;
        // TODO: Investigate if it need yet
        const groupColorIndex = getGroupColorIndex(
          item.rating,
          ratingGroups
        );
        const isFirst = isFirstInGroup(index, item.rating, ratingGroups)
        const isLast = isLastInGroup(index, item.rating, ratingGroups)
        return {
          username,
          rating: item.rating,
          avatar_icon: item.avatar_icon,
          avatar_effect: item.avatar_effect,
          rank,
          groupColorIndex,
          isFirst,
          isLast,
          isCurrentUser,
        }
      });

      if (!list.find((item) => item.username === currentUser.username)) {
        list.push(currentUser);
      }

      return list;
    },

    [leaderboard, currentUser, ratingGroups]
  )

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader className={styles.loader} />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.list}>
        {!leaderboard.length ? (
          <div className={styles.empty}>{t('battlePass.empty')}</div>
        ) : (
          leaderboardDataList.map((item, index) => {
            return item.isCurrentUser ? (
              <div
                key={index}
                data-current-user="true"
                ref={currentUserRef}
              >
                <SquadListItemRanking
                  rank={item.rank}
                  name={item.username}
                  score={item.rating}
                  avatarUrl={item.avatar_icon || DEFAULT_AVATAR_URL}
                  effectUrl={item.avatar_effect}
                  groupColorIndex={item.groupColorIndex}
                  isFirstInGroup={item.isFirst}
                  isLastInGroup={item.isLast}
                />
              </div>
            ) : (
              <SquadListItemDefault
                key={index}
                rank={item.rank}
                name={item.username}
                score={item.rating}
                avatarUrl={item.avatar_icon || DEFAULT_AVATAR_URL}
                effectUrl={item.avatar_effect}
                groupColorIndex={item.groupColorIndex}
                isFirstInGroup={item.isFirst}
                isLastInGroup={item.isLast}
              />
            )
          })
        )}
      </div>
      <AnimatePresence>
        {showPinnedBottom && currentUser && (
          <motion.div
            className={classNames(styles.pinnedBottom, {
              [styles.navbarVisible]: shouldShowNavbar,
            })}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
            }}
          >
            <SquadListItemRanking
              rank={currentUser.rank}
              name={currentUser.username}
              score={currentUser.rating}
              avatarUrl={
                currentUser.avatar_icon || DEFAULT_AVATAR_URL
              }
              effectUrl={currentUser.avatar_effect}
              groupColorIndex={getGroupColorIndex(
                currentUser.rating,
                ratingGroups
              )}
              isFirstInGroup={isFirstInGroup(
                currentUser.rank,
                currentUser.rating,
                ratingGroups
              )}
              isLastInGroup={isLastInGroup(
                currentUser.rank,
                currentUser.rating,
                ratingGroups
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
